/**
 * DocumentSearchPage
 * 
 * Advanced document search with filters and results
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, X, FileX } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { API_BASE_URL } from '@/core/lib/api-client';

import { useDocumentSearch } from '../hooks/useDocumentSearch';
import { Document } from '../types/documentsTypes';
import { DocumentGrid } from '../components/DocumentGrid';
import { DocumentList } from '../components/DocumentList';
import { DocumentFilters } from '../components/DocumentFilters';
import { DocumentViewer } from '../components/DocumentViewer';
import { useDocumentsStore } from '../stores/documentsStore';

// ============================================================================
// Component
// ============================================================================

export const DocumentSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialQuery = searchParams.get('q') || '';
  const { viewMode } = useDocumentsStore();

  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);

  const {
    query,
    setQuery,
    debouncedQuery,
    filters,
    setDocumentType,
    setChildId,
    setDateRange,
    clearFilters,
    results,
    isLoading,
    activeFilterCount,
  } = useDocumentSearch('current-family-id', { initialQuery });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  const handleDocumentClick = (document: Document) => {
    setViewingDocument(document);
  };

  const handleDocumentAction = (action: string, document: Document) => {
    switch (action) {
      case 'view':
        setViewingDocument(document);
        break;
      case 'edit':
        navigate(`/family/documents/${document.id}/edit`);
        break;
      case 'download':
        window.open(`${API_BASE_URL}/documents/${document.id}/download`, '_blank');
        break;
    }
  };

  const documents = results?.documents || [];
  const totalCount = results?.total || 0;

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <button
              onClick={() => navigate('/family/documents')}
              className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {/* Search Input */}
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search documents..."
                  className={cn(
                    'w-full pl-12 pr-10 py-3 rounded-xl border border-gray-200',
                    'focus:outline-none focus:ring-2 focus:ring-sage/20 focus:border-sage',
                    'text-lg'
                  )}
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-cream rounded-full"
                  >
                    <X className="w-4 h-4 text-warm-gray" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <DocumentFilters
            filters={filters}
            onFiltersChange={(newFilters) => {
              if (newFilters.document_type !== undefined) {
                setDocumentType(newFilters.document_type);
              }
              if (newFilters.child_id !== undefined) {
                setChildId(newFilters.child_id);
              }
              if (newFilters.date_from !== undefined || newFilters.date_to !== undefined) {
                setDateRange(newFilters.date_from || null, newFilters.date_to || null);
              }
            }}
          />
        </div>
      </div>

      {/* Results */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            {debouncedQuery ? (
              <h2 className="text-lg font-medium text-navy">
                {isLoading ? (
                  'Searching...'
                ) : (
                  <>
                    {totalCount} result{totalCount !== 1 ? 's' : ''} for "{debouncedQuery}"
                  </>
                )}
              </h2>
            ) : (
              <h2 className="text-lg font-medium text-navy">
                {activeFilterCount > 0 
                  ? `${totalCount} filtered document${totalCount !== 1 ? 's' : ''}`
                  : 'Enter a search term'}
              </h2>
            )}
          </div>
          
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="text-sm text-sage hover:text-sage-dark"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Results Grid */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          {!debouncedQuery && activeFilterCount === 0 ? (
            <div className="text-center py-16">
              <Search className="w-16 h-16 mx-auto text-warm-gray opacity-50 mb-4" />
              <h3 className="text-lg font-medium text-navy mb-2">Search your documents</h3>
              <p className="text-warm-gray">
                Enter keywords to search through your document vault
              </p>
            </div>
          ) : isLoading ? (
            viewMode === 'grid' ? (
              <DocumentGrid documents={[]} isLoading={true} />
            ) : (
              <DocumentList documents={[]} isLoading={true} />
            )
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <FileX className="w-16 h-16 mx-auto text-warm-gray opacity-50 mb-4" />
              <h3 className="text-lg font-medium text-navy mb-2">No documents found</h3>
              <p className="text-warm-gray">
                Try different keywords or adjust your filters
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <DocumentGrid
              documents={documents}
              onDocumentClick={handleDocumentClick}
              onDocumentAction={handleDocumentAction}
            />
          ) : (
            <DocumentList
              documents={documents}
              onDocumentClick={handleDocumentClick}
              onDocumentAction={handleDocumentAction}
            />
          )}
        </div>
      </main>

      {/* Document Viewer */}
      {viewingDocument && (
        <DocumentViewer
          document={viewingDocument}
          documents={documents}
          onClose={() => setViewingDocument(null)}
          onNavigate={setViewingDocument}
          onAction={handleDocumentAction}
        />
      )}
    </div>
  );
};

export default DocumentSearchPage;


